import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Trash2, MessageSquare, Phone, MapPin, Building2 } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "nuovo", label: "Nuovo" },
  { value: "letto", label: "Letto" },
  { value: "gestito", label: "Gestito" },
];
const STATUS_CLS = {
  nuovo: "bg-brema-red/15 text-brema-red",
  letto: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  gestito: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
};
const dmyhm = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

/** Messaggi liberi «Scrivi all'azienda» inviati dai rivenditori dal Portale B2B. */
export function ContactMessagesTab({ onCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/contact-requests").then(({ data }) => {
      setRows(data.requests || []);
      onCount && onCount(data.unread || 0);
    }).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setStatus = async (id, status) => {
    try { await api.patch(`/contact-requests/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Aggiornamento fallito"); }
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const deleteSelected = async () => {
    if (selected.length === 0) return;
    try {
      const { data } = await api.post("/contact-requests/delete-selected", { ids: selected });
      toast.success(`Eliminati ${data.deleted} messaggi`); setSelected([]); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Eliminazione fallita"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="contact-messages-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">Messaggi liberi inviati dai rivenditori con «Scrivi all'azienda» dal Portale B2B. Ogni messaggio genera anche una notifica e-mail al Commerciale (Impostazioni → Destinatari notifiche).</div>
        {selected.length > 0 && (
          <Button variant="danger" size="sm" onClick={deleteSelected} data-testid="contact-msg-delete-selected">
            <Trash2 className="mr-1.5 h-4 w-4" /> Elimina selezionati ({selected.length})
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-brema-card/50 py-14 text-center text-muted-foreground" data-testid="contact-msg-empty">
          <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-40" /> Nessun messaggio ricevuto dal Portale B2B.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const replyTo = r.contact_email || r.email;
            return (
              <div key={r.id} className={`rounded-xl border bg-brema-card p-4 ${r.status === "nuovo" ? "border-brema-red/40" : "border-border"}`} data-testid={`contact-msg-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`contact-msg-check-${r.id}`} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0 text-brand" />
                        <span className="font-semibold text-foreground" data-testid={`contact-msg-shop-${r.id}`}>{r.shop_name || r.email}</span>
                        <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[r.status] || STATUS_CLS.letto}`} data-testid={`contact-msg-status-badge-${r.id}`}>
                          {(STATUS_OPTIONS.find((o) => o.value === r.status) || {}).label || r.status}
                        </span>
                        {r.reseller_tier && (
                          <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.reseller_tier === "ufficiale" ? "bg-brema-red/15 text-brema-red" : "bg-positive/10 text-positive"}`}>
                            {r.reseller_tier === "ufficiale" ? "Ufficiale" : "Potenziale"}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{dmyhm(r.created_at)}</span>
                      </div>
                      <div className="mt-1.5 text-base font-semibold text-foreground" data-testid={`contact-msg-subject-${r.id}`}>{r.subject || "—"}</div>
                      <div className="mt-1 grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {replyTo}</span>
                        {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                        {r.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.city}</span>}
                        {r.vat_number && <span>P.IVA: <span className="text-foreground">{r.vat_number}</span></span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={r.status || "nuovo"} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="h-9 w-[140px]" data-testid={`contact-msg-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <a href={`mailto:${replyTo}?subject=${encodeURIComponent(`Re: ${r.subject || "Portale B2B"}`)}`} data-testid={`contact-msg-reply-${r.id}`}>
                      <Button variant="outline" size="sm"><Mail className="mr-1.5 h-4 w-4" /> Rispondi</Button>
                    </a>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap rounded-lg bg-brema-bg p-3 pl-3 text-sm text-foreground" data-testid={`contact-msg-body-${r.id}`}>{r.message}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
